import { UseQueryResult } from '@tanstack/react-query'
import {
	Dispatch,
	FC,
	SetStateAction,
	useEffect,
	useRef,
	useState
} from 'react'
import { Control, useForm } from 'react-hook-form'

import { useSnackbar } from '@/components/ui/snackbar/useSnackbar'

import { ShotlistsService } from '@/services/shotlist.service'

import { STEP_TYPE } from '@/constants/common'
import { PLATFORMS_CODES, PLATFROMS_LANGUAGES } from '@/constants/languages'
import { useFormUploaderTaskSession } from '@/store/features/formUploader/useFormUploaderTaskSession'
import { IOption } from '@/types/selectOption.interface'
import { IOriginal, IShotlist } from '@/types/shotlist.interface'
import { ILanguage, TStatus } from '@/types/task.interface'
import {
	IPlatform,
	ISelectedPlatform,
	IUploaderForm,
	TPlatformProcessingType,
	TPlatformResult,
	TPlatformValue
} from '@/types/uploadForm.interface'

import { autoFill } from './actions/autoFill'
import { stopTranscription } from './actions/stopTranscription'
import { handleUploadTranscribe } from './actions/transcribe'
import { handleUploadTranslate } from './actions/translate'
import { uploadSrt } from './actions/uploadSrt'
import { uploadSrtError } from './actions/uploadSrtError'
import { watchTaskUntilFinished } from './actions/watchTaskUntilFinished'
import { FormUploaderLayout } from './components/FormUploaderLayout'
import { buildAvailableSourceLanguages } from './helpers/buildAvailableSourceLanguages'
import { getShotlistLanguages } from './helpers/getShotlistLanguages'
import { resetPlatformHeaderState } from './helpers/resetPlatformHeaderState'
import { useFormUploaderBootstrap } from './hooks/useFormUploaderBootstrap'
import { usePlatforms } from './hooks/usePlatforms'
import { getDefaultFormValues } from './utils/formDefaults'

interface IFormUploaderProps {
	setIsModalOpen: (open: boolean) => void
	control: Control<{ language: ILanguage }>
	getValues: () => { language: ILanguage }
	taskId: string
	languages: ILanguage[]
	onNextClick: (language: string) => void
	selectedWay: string
	setSelectedWay: (selectedWay: string) => void
	openFormModal: boolean
	setOpenFormModal: (open: boolean) => void
	status?: string
	error?: boolean
	isUpload?: boolean
	isSuccess?: boolean
	isMP4?: boolean
	filename?: string
	isUploading?: boolean
	isTranscribing?: boolean
	selectedTranscribingLang?: ILanguage | null
	setSelectedTranscribingLang: Dispatch<SetStateAction<ILanguage | null>>
	isTranslating: boolean
	setIsTranslating: Dispatch<SetStateAction<boolean>>
	selectedPlatformList: ISelectedPlatform[]
	setSelectedPlatformList: Dispatch<SetStateAction<ISelectedPlatform[]>>
	refetchShotlist?: UseQueryResult<IShotlist, unknown>['refetch']
	shotlist?: IShotlist | null
	startPlatformProcessing: (
		value: TPlatformValue,
		processing: TPlatformProcessingType
	) => void
	finishPlatformAutoFilled: (value: TPlatformValue) => void
	finishPlatformDone: (value: TPlatformValue, result: TPlatformResult) => void
	finishPlatformError: (value: TPlatformValue, errorText?: string) => void
	clearWatch: () => void
	setIsTranscribing?: Dispatch<SetStateAction<boolean>>
	deleteTranslation: (id: number) => Promise<unknown>
	setIsLoading?: Dispatch<SetStateAction<boolean>>
	availableShotlistLanguages?: IOriginal[]
	setIsSrtUploading?: Dispatch<SetStateAction<boolean>>
	watchTask: (
		taskId: string,
		onUpdate?: (shotlist: IShotlist) => void,
		onFinished?: (shotlist: IShotlist) => void
	) => Promise<void>
	clearTimer?: () => void
	setIsUploading?: Dispatch<SetStateAction<boolean>>
	updateDraftStatus?: (id: string, status: TStatus) => void
	setLocalHasVideo?: Dispatch<SetStateAction<boolean>>
}
const FormUploader: FC<IFormUploaderProps> = ({
	setIsModalOpen,
	control,
	getValues,
	taskId,
	languages,
	onNextClick,
	selectedWay,
	setSelectedWay,
	status,
	error,
	openFormModal,
	setOpenFormModal,
	isUpload,
	isSuccess,
	isMP4,
	filename,
	isUploading,
	isTranscribing,
	selectedTranscribingLang,
	setSelectedTranscribingLang,
	isTranslating,
	setIsTranslating,
	selectedPlatformList,
	setSelectedPlatformList,
	refetchShotlist,
	shotlist,
	startPlatformProcessing,
	finishPlatformAutoFilled,
	finishPlatformDone,
	finishPlatformError,
	clearWatch,
	setIsTranscribing,
	deleteTranslation,
	setIsLoading,
	availableShotlistLanguages,
	setIsSrtUploading,
	watchTask,
	clearTimer,
	setIsUploading,
	updateDraftStatus,
	setLocalHasVideo
}) => {
	const [firstPlatform, setFirstPlatform] =
		useState<IPlatform['value']>('english')
	const [pendingTranscribing, setPendingTranscribing] = useState<string | null>(
		null
	)
	const [availableSourceLanguages, setAvailableSourceLanguages] = useState<
		IOption[]
	>([])

	// TODO!!! Remove all the states for FormUploader to the store!!!

	const [preferredAutoFillSourcePlatform, setPreferredAutoFillSourcePlatform] =
		useState<TPlatformValue | ''>('')

	const {
		selectedPlatform,
		setSelectedPlatform,
		pendingSrtFileName,
		setPendingSrtFileName,
		isFormUploading,
		setIsFormUploading,
		isUploaded,
		setIsUploaded
	} = useFormUploaderTaskSession(taskId)

	const { platforms, isPlatformsLoading } = usePlatforms()

	const firstPlatformLabel =
		platforms?.find(p => p.value === firstPlatform)?.label || 'English'

	const modalRef = useRef<HTMLDivElement>(null)
	const { setContainer } = useSnackbar()

	const targetLanguage = languages.find(
		lang => lang.language === PLATFROMS_LANGUAGES[selectedPlatform]
	)?.code

	const {
		control: uploaderFormControl,
		getValues: uploaderFormGetValues,
		watch: uploaderFormWatch,
		setValue: uploaderFormSetValue,
		formState,
		reset: uploaderFormReset,
		trigger
	} = useForm<IUploaderForm>({
		mode: 'onChange',
		defaultValues: getDefaultFormValues()
	})

	const { currentShotlist, serverHasVideo, refreshCurrentShotlist } =
		useFormUploaderBootstrap({
			taskId,
			shotlist,
			openFormModal,
			selectedPlatform,
			setSelectedPlatform,
			selectedPlatformList,
			setSelectedPlatformList,
			refetchShotlist
		})

	useEffect(() => {
		if (modalRef.current) {
			setContainer(modalRef.current)
		}
		return () => {
			setContainer(null)
		}
	}, [setContainer])

	useEffect(() => {
		const html = document.documentElement
		const body = document.body

		const previousHtmlOverflow = html.style.overflow
		const previousBodyOverflow = body.style.overflow
		const previousHtmlPaddingRight = html.style.paddingRight
		const previousBodyPaddingRight = body.style.paddingRight

		const scrollbarWidth = window.innerWidth - html.clientWidth

		html.style.overflow = 'hidden'
		body.style.overflow = 'hidden'

		if (scrollbarWidth > 0) {
			const scrollbarWidthPx = `${scrollbarWidth}px`
			html.style.paddingRight = scrollbarWidthPx
			body.style.paddingRight = scrollbarWidthPx
		}

		return () => {
			html.style.overflow = previousHtmlOverflow
			body.style.overflow = previousBodyOverflow
			html.style.paddingRight = previousHtmlPaddingRight
			body.style.paddingRight = previousBodyPaddingRight
		}
	}, [])

	const availableAutoFillPlatforms = selectedPlatformList.filter(
		platform => platform.value !== selectedPlatform
	)

	const effectiveAutoFillSourcePlatform: TPlatformValue | '' =
		availableAutoFillPlatforms.find(
			platform => platform.value === preferredAutoFillSourcePlatform
		)?.value ||
		availableAutoFillPlatforms[0]?.value ||
		''

	useEffect(() => {
		if (!pendingTranscribing || isUploading || isTranscribing) return

		const lang = pendingTranscribing
		setPendingTranscribing(null)

		onTranscribe(lang)
	}, [pendingTranscribing, isUploading, isTranscribing])

	useEffect(() => {
		setAvailableSourceLanguages(
			buildAvailableSourceLanguages(
				selectedTranscribingLang,
				selectedPlatformList,
				languages
			)
		)
	}, [selectedTranscribingLang, selectedPlatformList, languages])

	const handleNextClick = () => {
		if (!taskId) return

		try {
			const values = getValues()
			const language = values.language?.code

			if (!language) return
			onNextClick(language)

			if (selectedWay === STEP_TYPE.UPLOAD) {
				setOpenFormModal(true)
			}
		} catch (error) {
			console.error('Transcription error:', error)
		}
	}

	const onWatchUntilFinished = () => {
		if (!taskId) return Promise.reject(new Error('No shotlist id'))

		return watchTaskUntilFinished({
			taskId,
			watchTask,
			clearWatch,
			onProgress: shotlist => {
				updateDraftStatus?.(taskId, shotlist.status)
				setLocalHasVideo?.(shotlist.has_video)
			},
			onFinally: shotlist => {
				setIsTranscribing(false)
				clearTimer?.()
				setIsUploading?.(false)
				updateDraftStatus?.(taskId, shotlist.status)
				setLocalHasVideo?.(shotlist.has_video)
			}
		})
	}

	const onTranscribe = async (language: string) => {
		if (!language || !selectedPlatform || !selectedTranscribingLang) return

		startPlatformProcessing(selectedPlatform, 'transcribe')

		if (isUploading) {
			setPendingTranscribing?.(language)
			return
		}

		const safeTarget = targetLanguage || ''

		try {
			setIsTranscribing?.(true)
			await handleUploadTranscribe(
				taskId,
				language,
				selectedPlatform,
				selectedTranscribingLang,
				safeTarget,
				setIsTranslating,
				onWatchUntilFinished,
				{
					onTranslateStart: () =>
						startPlatformProcessing(selectedPlatform, 'translate'),
					onDone: result => finishPlatformDone(selectedPlatform, result),
					onError: msg => finishPlatformError(selectedPlatform, msg)
				}
			)

			await refreshCurrentShotlist()
		} catch (error) {
			finishPlatformError(selectedPlatform, 'Something went wrong')
			console.log(error)
		} finally {
			setSelectedTranscribingLang(null)
			setIsTranscribing?.(false)
		}
	}

	const onUploadSrtError = () => {
		if (!selectedPlatform) return

		uploadSrtError({
			selectedPlatform,
			setSelectedPlatformList
		})
	}

	const onUploadSrt = async (file: File) => {
		if (!selectedPlatform) return

		startPlatformProcessing(selectedPlatform, 'transcribe')
		setIsSrtUploading?.(true)
		setPendingSrtFileName(file.name)

		try {
			await uploadSrt(
				taskId,
				file,
				languages,
				selectedPlatform,
				setSelectedPlatformList
			)

			await refreshCurrentShotlist()
		} catch (error) {
			console.log(error)
			finishPlatformError(selectedPlatform, 'Something went wrong')
			onUploadSrtError()
		} finally {
			setPendingSrtFileName('')
			setIsSrtUploading?.(false)
		}
	}

	const onTranslate = async (sourceLanguage: string) => {
		if (!taskId) return

		if (
			!targetLanguage ||
			!selectedPlatform ||
			targetLanguage === sourceLanguage
		)
			return

		startPlatformProcessing(selectedPlatform, 'translate')

		try {
			await handleUploadTranslate(
				taskId,
				targetLanguage,
				sourceLanguage,
				setIsTranslating,
				{
					onDone: result => finishPlatformDone(selectedPlatform, result),
					onError: msg => finishPlatformError(selectedPlatform, msg)
				}
			)

			await refreshCurrentShotlist()
		} catch (error) {
			finishPlatformError(selectedPlatform, 'Something went wrong')
			console.log(error)
		}
	}

	const handleDeleteTranslation = async () => {
		if (!selectedPlatform) return

		const currentShotlist = await ShotlistsService.getShotlist(taskId)
		const languages = [
			currentShotlist?.original,
			...(currentShotlist?.translations || [])
		].filter(Boolean)
		const platformLanguage = PLATFORMS_CODES[selectedPlatform]
		const id =
			languages.find(lang => lang.language === platformLanguage)?.id ||
			(await getShotlistLanguages({
				taskId,
				selectedPlatform,
				availableShotlistLanguages
			}))

		if (!id) return

		try {
			setIsLoading?.(true)

			await deleteTranslation(id)

			const isFirstPlatform =
				selectedPlatformList[0]?.value === selectedPlatform
			const shouldDeleteOriginal =
				isFirstPlatform &&
				currentShotlist?.original?.id !== id &&
				currentShotlist?.original?.language !== platformLanguage

			if (shouldDeleteOriginal) {
				await deleteTranslation(currentShotlist.original.id)
			}

			resetPlatformHeaderState({ selectedPlatform, setSelectedPlatformList })

			await refreshCurrentShotlist()
		} catch (error) {
			console.error('Deleting error:', error)
		} finally {
			setIsLoading?.(false)
		}
	}

	const onStopTranscription = async () => {
		if (!taskId) return

		await stopTranscription({
			taskId,
			clearWatch,
			selectedPlatform,
			setSelectedPlatformList,
			setSelectedTranscribingLang,
			setIsTranscribing
		})
	}

	const handleAutoFill = async () => {
		if (!selectedPlatform || !effectiveAutoFillSourcePlatform) return

		await autoFill({
			taskId,
			selectedPlatform,
			sourceAutoFillLang: effectiveAutoFillSourcePlatform,
			startPlatformProcessing,
			finishPlatformAutoFilled,
			setValue: uploaderFormSetValue
		})
	}

	return (
		<FormUploaderLayout
			modalRef={modalRef}
			selectedWay={selectedWay}
			setSelectedWay={setSelectedWay}
			setIsModalOpen={setIsModalOpen}
			status={status}
			openFormModal={openFormModal}
			isUploaded={isUploaded}
			isUpload={isUpload}
			isFormUploading={isFormUploading}
			handleNextClick={handleNextClick}
			platforms={platforms}
			isPlatformsLoading={isPlatformsLoading}
			firstPlatform={firstPlatform}
			setFirstPlatform={setFirstPlatform}
			uploaderFormControl={uploaderFormControl}
			uploaderFormGetValues={uploaderFormGetValues}
			uploaderFormWatch={uploaderFormWatch}
			uploaderFormSetValue={uploaderFormSetValue}
			formState={formState}
			uploaderFormReset={uploaderFormReset}
			trigger={trigger}
			error={error}
			taskId={taskId}
			setIsUploaded={setIsUploaded}
			setIsFormUploading={setIsFormUploading}
			isSuccess={isSuccess}
			serverHasVideo={serverHasVideo}
			firstPlatformLabel={firstPlatformLabel}
			languages={languages}
			isUploading={isUploading}
			isTranscribing={isTranscribing}
			onTranscribe={onTranscribe}
			onTranslate={onTranslate}
			onUploadSrt={onUploadSrt}
			selectedTranscribingLang={selectedTranscribingLang}
			setSelectedTranscribingLang={setSelectedTranscribingLang}
			isTranslating={isTranslating}
			selectedPlatform={selectedPlatform}
			setSelectedPlatform={setSelectedPlatform}
			selectedPlatformList={selectedPlatformList}
			setSelectedPlatformList={setSelectedPlatformList}
			availableSourceLanguages={availableSourceLanguages}
			setAvailableSourceLanguages={setAvailableSourceLanguages}
			refreshCurrentShotlist={refreshCurrentShotlist}
			currentShotlist={currentShotlist}
			pendingSrtFileName={pendingSrtFileName}
			handleDeleteTranslation={handleDeleteTranslation}
			onStopTranscription={onStopTranscription}
			handleAutoFill={handleAutoFill}
			effectiveAutoFillSourcePlatform={effectiveAutoFillSourcePlatform}
			setPreferredAutoFillSourcePlatform={setPreferredAutoFillSourcePlatform}
			isMP4={isMP4}
			filename={filename}
			control={control}
			setOpenFormModal={setOpenFormModal}
			onNextClick={onNextClick}
		/>
	)
}

export default FormUploader
